import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { BsGoogle, BsMicrosoft } from 'react-icons/bs';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import { Box, Button, Divider, Flex, Text, VStack } from '@chakra-ui/react';
import userflow from 'userflow.js';

import FormInput from '../../../components/form/FormInput';
import { inputTypes } from '../../../components/form/FormInput/util';
import LoadingSpinner from '../../../components/Spinner';
import LocalStorageService from '../../../services/local-storage-service';
import { CypressIds } from '../../../constants/cypressIds';
import { AgentPortalDuskIds } from '../../../constants/duskIds';
import { getValidationObject } from '../../../constants/regexPatterns';
import * as UserLevels from '../../../constants/userLevels';
import { userflowConstants } from '../../../constants/userflowConstants';
import {
  getInsuranceRequestInsured,
  selectInsured,
  setAgentPortalUser,
  verifySubmitterEmail
} from '../../../redux/modules/agentPortal';
import { createErrorToast, createSuccessToast } from '../../../redux/modules/app';
import { useAddAgentToInsured } from '../../../services/insurance-request-service';
import { useMeData } from '../../../services/user-service';
import colors from '../../../styles/theme/foundation/colors';

import MessageBlock from './messageBlock';

const VerifyEmailPage = () => {
  const dispatch = useDispatch();
  const { insuranceRequestId } = useParams();
  const history = useHistory();
  const { url } = useRouteMatch();
  const useFormMethods = useForm();
  const { handleSubmit } = useFormMethods;

  // look for microsoft admin consent or sso errors
  const urlQueryParams = new URLSearchParams(window.location.search);
  const microsoftAdminConsent = urlQueryParams.get('microsoft_admin_consent');
  const errors = urlQueryParams.get('error');

  useEffect(() => {
    if (microsoftAdminConsent === 'success') {
      dispatch(createSuccessToast({ title: 'Microsoft Admin Consent Successful', description: 'You can now log in to PINS.' }));
    }

    if (errors === 'access_denied') {
      dispatch(createErrorToast({ title: 'SSO Access Denied', description: 'Failed to gain access from Microsoft' }));
    }

    if (errors === 'consent_required') {
      dispatch(createErrorToast({ title: 'SSO Access Denied', description: 'Admin Consent Required' }));
    }

    return () => {};
  }, [errors, microsoftAdminConsent]);

  const [hideForm, setHideForm] = useState(false);
  const [message, setMessage] = useState(null);
  const [messageColor, setMessageColor] = useState(colors.greyScale['900']);
  const [loader, setLoader] = useState(false);
  const [redirectError, setRedirectError] = useState(false);
  const insured = useSelector(selectInsured);

  const { data: me, isLoading: meLoading } = useMeData();
  // Guards the mount-time auto-forward so it can only ever fire once per page
  // load (a re-render from the state changes inside doRedirect must not kick
  // off a second addAgentToInsured round trip).
  const autoRedirectStarted = useRef(false);

  const { mutateAsync: addAgentToInsured } = useAddAgentToInsured({ insuranceRequestId });

  // userFlow initiation
  useEffect(() => {
    userflow.init(userflowConstants.token);
    if (me) {
      userflow.identify(me.userId, {
        user_id: me.userId,
        name: `${me.userFirstName} ${me.userLastName}`,
        email: me.userEmail,
        userLevel: me.userLevel,
        userRoles: ['agent'],
        pinsVersion: { set_once: '2.0' },
        last_signed_in_at: new Date().toISOString() // ISO 8601 format
      });

      userflow.group('agents');
    }
  }, [me]);

  const doRedirect = async () => {
    // Already-authenticated agents are auto-forwarded: show a "starting" state
    // and swap to an inline error + retry if the network call fails, rather
    // than silently falling back to the (misleading) verify-email form.
    setRedirectError(false);
    setHideForm(true);
    setMessageColor(colors.greyScale['900']);
    setMessage('Starting your submission...');
    setLoader(true);

    try {
      const newInsuranceRequest = await addAgentToInsured();
      window.location.href = `${window.location.origin}/v2/insurance-requests/${newInsuranceRequest.id}/email-verification`;
    } catch (e) {
      // onError toast is dispatched by the mutation; show an inline retry here.
      setLoader(false);
      setMessage(null);
      setRedirectError(true);
    }
  };

  const handleRedirect = async () => {
    await doRedirect();
  };

  useEffect(() => {
    dispatch(getInsuranceRequestInsured(insuranceRequestId));
  }, []);

  // This page is a landing page first and a form second: an agent who is
  // already authenticated (their portal-api token/JWT cookie resolves /me)
  // has nothing left to verify, so grant them the agent role on this
  // insurance-request's insured and forward them into the submission flow.
  //
  // The decision is made purely on /me. It deliberately does NOT wait on
  // GET insurance-requests/{id}, because that endpoint authorizes against the
  // insured the agent has not been granted access to yet — for a first-time
  // agent it always answers 403, which is exactly the case we want to redirect.
  useEffect(() => {
    if (meLoading || autoRedirectStarted.current) {
      return;
    }

    if (me?.userId && me?.userLevel === UserLevels.Agent) {
      autoRedirectStarted.current = true;
      // user already authenticated, so hit route to officially add them to insured
      handleRedirect();
    }
  }, [me, meLoading]);

  const onSubmit = async data => {
    const response = await dispatch(verifySubmitterEmail(insuranceRequestId, data.email));

    if (response?.message === 'User exists and is not an agent') {
      history.push(url.replace('verify', 'cert-holder-user'));
      return;
    }

    if (!response?.error && data.email === response.user.user_email) {
      const { user } = response;
      setHideForm(true);
      setMessageColor(colors.success.primary['600']);
      setMessage('Email Verified!  Sending you to the submission portal...');
      setLoader(true);

      const today = new Date();
      const tomorrow = today.setDate(today.getDate() + 1);
      LocalStorageService.store('portal-api-token', response.token);
      LocalStorageService.store('portal-api-token-date', tomorrow);
      window.heap.identify(user.user_email);
      window.heap.addUserProperties({
        email: user.user_email,
        isSubmitter: true,
        userId: user.user_id,
        pinsVersion: '2.0'
      });

      history.push(url.replace('verify', 'submission'));
    }

    if (response?.error) {
      setHideForm(true);
      setMessageColor(colors.greyScale['900']);
      setMessage(response.error);
    }
  };

  // Hold the spinner while /me is in flight so an already-authenticated agent
  // never sees a flash of the (irrelevant) verify-email form before being
  // forwarded.
  if (!insured || meLoading) {
    return (<LoadingSpinner isInline minHeight="100px" />);
  }

  return (
    <Flex direction="column" justifyContent="center" minH="50vh">
      {!hideForm && (
        <form onSubmit={handleSubmit(onSubmit)}>
          <Text mb={8} textStyle="displayMd">
            Welcome to PINS Advantage!
          </Text>

          <Text fontSize="bodyXl" mb={4}>
            You are about to submit insurance for
          </Text>

          <Text mb={4} textStyle="bodyXl600">
            {insured.insuredCompanyName}
            .
          </Text>

          <Text fontSize="bodyXl" mb={4}>
            Please verify your email address to get started.
          </Text>
          <Flex
            alignItems="center"
            flexFlow="row wrap"
            justifyContent="center"
            mb="20px"
            minH="100px"
          >
            <FormInput
              cypressId={CypressIds.certEmailInput}
              data-analytics-id="agent-portal-verify-email-input"
              duskSelector={AgentPortalDuskIds.verifyEmailInput}
              inputName="email"
              labelName="Email Address"
              maxW="400px"
              mx={4}
              placeholder="email address"
              type={inputTypes.standard}
              useFormMethods={useFormMethods}
              validationRules={{ ...getValidationObject('emailAddress'), required: 'Please provide an email address.' }}
            />
            <Button
              data-analytics-id="agent-portal-verify-email-submit-button"
              data-cy={CypressIds.certValidateEmailButton}
              dusk={AgentPortalDuskIds.verifyEmailButton}
              type="submit"
            >
              Verify
            </Button>
          </Flex>

          <Box
            mx="auto"
            my="auto"
            pb={8}
            w="400px"
          >
            <Flex align="center" mb={6}>
              <Divider borderColor="gray.600" />
              <Text color="gray.600" fontSize="sm" mx={3}>
                OR
              </Text>
              <Divider borderColor="gray.600" />
            </Flex>

            <VStack>
              <Button
                data-cy={CypressIds.agentGoogleSSOButton}
                leftIcon={<BsGoogle />}
                my={1}
                variant="secondary"
                onClick={async () => {
                  if (me?.userId) {
                    // user already authenticated, so hit route to add them to insured
                    try {
                      const newInsuranceRequest = await addAgentToInsured();

                      window.location.href = `${window.location.origin}/v2/insurance-requests/${newInsuranceRequest.id}/email-verification`;
                    } catch (e) {
                      // onError toast already shown; leave the button clickable to retry.
                    }

                    return;
                  }

                  window.location.href = `${window.location.origin}/api/v1/auth/google/redirect?source=${insuranceRequestId}`;
                }}
              >
                Verify with Google
              </Button>

              <Button
                data-cy={CypressIds.agentMicrosoftSSOButton}
                leftIcon={<BsMicrosoft />}
                my={1}
                variant="secondary"
                onClick={async () => {
                  if (me?.userId) {
                    // user already authenticated, so hit route to add them to insured
                    try {
                      const newInsuranceRequest = await addAgentToInsured();

                      window.location.href = `${window.location.origin}/v2/insurance-requests/${newInsuranceRequest.id}/email-verification`;
                    } catch (e) {
                      // onError toast already shown; leave the button clickable to retry.
                    }

                    return;
                  }

                  window.location.href = `${window.location.origin}/api/v1/auth/microsoft/redirect?source=${insuranceRequestId}`;
                }}
              >
                Verify with Outlook
              </Button>
            </VStack>
          </Box>
        </form>
      )}

      {message && <MessageBlock color={messageColor} loading={loader} message={message} />}

      {redirectError && (
        <VStack align="center" data-cy={CypressIds.certRedirectError} spacing={4}>
          <Text color={colors.greyScale['900']} textAlign="center">
            We couldn&apos;t start your submission. Please check your connection and try again.
          </Text>
          <Button data-cy={CypressIds.certRedirectRetryButton} onClick={handleRedirect}>
            Try again
          </Button>
        </VStack>
      )}

    </Flex>
  );
};

export default VerifyEmailPage;
